// ---------------------------------------------------------------------------
// Test Mode — simulated company profile (CLE-250/253).
//
// "Clean Code Culture SRL" — a Cluj-Napoca software development agency with
// ~500.000 EUR annual revenue and ~200.000+ EUR net profit. This module holds
// the company-wide constants and a few date helpers used to build all the
// coherent per-entity datasets in this folder.
//
// Currency convention across the simulated data: amounts are stored in RON
// (the app's native financial currency) unless a type explicitly carries EUR.
// The ~500k EUR ≈ 2.5M RON figure drives the financial totals.
// ---------------------------------------------------------------------------

export const TEST_COMPANY = {
  name: 'Clean Code Culture SRL',
  domain: 'Software development agency (web, mobile, SaaS)',
  location: 'Cluj-Napoca, România',
  cui: 'RO12345678',
  annualRevenueEur: 500_000,
  annualRevenueRon: 2_500_000,
  currency: 'RON',
  eurToRon: 5, // simplified, stable conversion for the simulated numbers
} as const;

// Email domain used for all simulated team members. Centralised so the brand
// stays consistent across projects / people / invoices.
export const EMAIL_DOMAIN = 'cleancodeculture.ro';

// A fixed "today" anchor keeps the simulated data stable and coherent across
// renders. We derive it from the real current date so relative views (today /
// tomorrow / this week) still look alive, but snap to midnight for determinism.
export function testToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// ISO date (YYYY-MM-DD) offset from the test "today" by `days`.
export function isoDate(offsetDays = 0): string {
  const d = testToday();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

// Full ISO timestamp offset from the test "today" by `days` (+ optional hour).
export function isoDateTime(offsetDays = 0, hour = 9, minute = 0): string {
  const d = testToday();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

// Convert EUR to RON using the fixed simulated rate.
export function eur(amount: number): number {
  return Math.round(amount * TEST_COMPANY.eurToRon);
}
